import React, { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { FormProvider, useForm } from 'react-hook-form';
import t from 'react-translate';

import { MetadataFieldType } from 'redux/schemas/models/org-level-metadata-settings';
import NvCheckboxDropdown, { NvCheckboxDropdownItem } from 'shared/components/inputs/nv-checkbox-dropdown';
import { danger } from 'styles/global_defaults/colors';

type CourseMetadataTag = {
  metadataFieldId: number,
  metadataValueId: number,
};

type Props = {
  field: MetadataFieldType,
  courseMetadataTags: CourseMetadataTag[],
  setMetadataTags: (tags: CourseMetadataTag[]) => void,
  setRequiredField?: (name: string, value: any) => void,
  removeRequiredField?: (name: string) => void,
  showError?: boolean,
  nvDropDownName: string,
};

/**
 * Multi-select variant of the course/journey metadata picker: renders a metadata category's
 * values as a checkbox dropdown, allowing more than one value per category. Single-select
 * categories keep using MetadataNvDropdown. Wraps the shared NvCheckboxDropdown in its own
 * react-hook-form context (mirrors the skill-tags picker) and syncs selections back into the
 * flat courseMetadata array via setMetadataTags.
 */
const MetadataCheckboxDropdown = ({
  field,
  courseMetadataTags,
  setMetadataTags,
  setRequiredField,
  removeRequiredField,
  showError = false,
  nvDropDownName,
}: Props) => {
  const { required } = field;

  const items: NvCheckboxDropdownItem[] = useMemo(
    () => (field.metadataValues ?? []).map((value) => ({
      id: value.id as number,
      name: value.value,
      label: value.value,
    })),
    [field.metadataValues],
  );

  const selectedTagsForField = courseMetadataTags.filter((tag) => tag.metadataFieldId === field.id);
  const selectedItems = items.filter(
    (item) => selectedTagsForField.some((tag) => tag.metadataValueId === item.id),
  );

  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      [nvDropDownName]: selectedItems,
    },
  });

  useEffect(() => {
    if (required && setRequiredField) {
      setRequiredField(nvDropDownName, selectedTagsForField.length ? selectedTagsForField : undefined);
    }
    if (!required && removeRequiredField) {
      removeRequiredField(nvDropDownName);
    }
  }, [required]);

  const onSelectionChanged = (
    item: NvCheckboxDropdownItem,
    index: number,
    values: NvCheckboxDropdownItem[] = [],
  ) => {
    const otherFieldTags = courseMetadataTags.filter((tag) => tag.metadataFieldId !== field.id);
    const thisFieldTags = values.map((value) => ({
      metadataFieldId: field.id as number,
      metadataValueId: value.id as number,
    }));
    if (required && setRequiredField) {
      setRequiredField(nvDropDownName, thisFieldTags.length ? thisFieldTags : undefined);
    }
    setMetadataTags([...otherFieldTags, ...thisFieldTags]);
  };

  const errorStyles = css`
    .bs4-dropdown {
      border-color: ${danger};
    }
  `;

  return (
    <FormProvider {...methods}>
      <div css={showError ? errorStyles : undefined}>
        <NvCheckboxDropdown
          name={nvDropDownName}
          items={items}
          onChange={onSelectionChanged}
          prompt={t.COURSES.FORM.ADDITIONAL_METADATA.PLEASE_SELECT_METADATA_TAG()}
        />
      </div>
    </FormProvider>
  );
};

export default MetadataCheckboxDropdown;
